import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/client.js';
import { useStores } from '../hooks/useStores.js';
import Spinner from './Spinner.jsx';

const fmt    = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat('es-AR').format(n || 0);

const STATUS_OPTIONS = [
  { value: '',          label: 'Todos' },
  { value: 'processed', label: 'Acreditadas' },
  { value: 'canceled',  label: 'Canceladas' },
  { value: 'expired',   label: 'Expiradas' },
];
const STATUS_SERIES = [
  { status: 'processed', color: '#10b981', label: 'Acreditadas' },
  { status: 'canceled',  color: '#ef4444', label: 'Canceladas'  },
  { status: 'expired',   color: '#f59e0b', label: 'Expiradas'   },
];
const STATUS_COLOR = { processed: '#10b981', canceled: '#ef4444', expired: '#f59e0b', '': '#10b981' };

const PM_OPTIONS = [
  { value: '',             label: 'Todos los medios' },
  { value: 'credit_card',  label: 'Crédito' },
  { value: 'debit_card',   label: 'Débito' },
  { value: 'qr',           label: 'QR' },
];
const PM_SERIES = [
  { pm: 'credit_card', color: '#6366f1', label: 'Crédito' },
  { pm: 'debit_card',  color: '#0ea5e9', label: 'Débito'  },
  { pm: 'qr',          color: '#f59e0b', label: 'QR'      },
];

const DAYS_OPTIONS = [{ value: 7, label: '7d' }, { value: 30, label: '30d' }, { value: 90, label: '90d' }];

function fetchSeries({ clientId, storeId, status, paymentMethod, granularity, days }) {
  const q = new URLSearchParams();
  if (clientId)      q.set('clientId', clientId);
  if (storeId)       q.set('storeId', storeId);
  if (status)        q.set('status', status);
  if (paymentMethod) q.set('paymentMethod', paymentMethod);
  if (granularity)   q.set('granularity', granularity);
  if (days)          q.set('days', days);
  return apiGet(`/orders/time-series?${q.toString()}`);
}

function useSeries(params) {
  return useQuery({
    queryKey: ['time-series', params.clientId, params.storeId, params.status, params.paymentMethod, params.granularity, params.days],
    queryFn: () => fetchSeries(params),
    staleTime: 2 * 60 * 1000,
    enabled: params.enabled !== false,
  });
}

/* ── SVG chart ─────────────────────────────────────────────────────── */
// padL mínimo para que el área de datos use todo el ancho
const W = 560, H = 175, padL = 2, padR = 2, padT = 18, padB = 28;
const innerW = W - padL - padR;
const innerH = H - padT - padB;

function buildPath(data, visMin, visRange, maxAmt, close = false) {
  if (!data?.length) return '';
  const tx = (ts) => padL + ((new Date(ts).getTime() - visMin) / visRange) * innerW;
  const ty = (v)  => padT + innerH - (v / maxAmt) * innerH;
  const pts = data.map(d => ({ x: tx(d.ts), y: ty(parseFloat(d.amount)) }));
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  if (!close) return line;
  const bL = pts[0].x.toFixed(1), bR = pts[pts.length - 1].x.toFixed(1), bot = (padT + innerH).toFixed(1);
  return `${line} L${bR},${bot} L${bL},${bot} Z`;
}

function LineChart({ series, granularity }) {
  const svgRef   = useRef(null);
  const [view, setView]   = useState({ xMin: 0, xMax: 1 });
  const [hover, setHover] = useState(null);
  const dragging = useRef(false);
  const lastX    = useRef(0);

  const seriesKey = series.map(s => s.status).join(',');
  useEffect(() => { setView({ xMin: 0, xMax: 1 }); setHover(null); }, [seriesKey, granularity]);

  // Liberar drag si el mouse sale de la ventana
  useEffect(() => {
    const stop = () => { dragging.current = false; };
    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, []);

  const allData = series.flatMap(s => s.data || []);
  if (!allData.length) return (
    <p className="text-sm py-10 text-center" style={{ color: 'var(--text-muted)' }}>Sin datos para el período</p>
  );

  const times  = allData.map(d => new Date(d.ts).getTime());
  const tMin   = Math.min(...times);
  const tMax   = Math.max(...times);
  const tRange = tMax - tMin || 1;
  const visMin   = tMin + view.xMin * tRange;
  const visMax   = tMin + view.xMax * tRange;
  const visRange = visMax - visMin || 1;
  const maxAmt   = Math.max(...allData.map(d => parseFloat(d.amount)), 1);

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({
    y: padT + innerH - f * innerH,
    label: fmt(f * maxAmt),
  }));

  // X labels — de todas las series, deduplicados por ts
  const seen = new Set();
  const uniqTs = allData.filter(d => { if (seen.has(d.ts)) return false; seen.add(d.ts); return true; })
    .filter(d => { const t = new Date(d.ts).getTime(); return t >= visMin - visRange * 0.05 && t <= visMax + visRange * 0.05; })
    .sort((a, b) => new Date(a.ts) - new Date(b.ts));
  const xStep = Math.max(1, Math.floor(uniqTs.length / 5));
  const xLabels = uniqTs.filter((_, i) => i % xStep === 0 || i === uniqTs.length - 1).map(d => {
    const dt = new Date(d.ts);
    const x  = padL + ((new Date(d.ts).getTime() - visMin) / visRange) * innerW;
    return {
      x: Math.min(Math.max(x, padL + 4), W - padR - 4),
      label: granularity === 'hour'
        ? `${dt.getDate()}/${dt.getMonth() + 1} ${String(dt.getHours()).padStart(2, '0')}h`
        : `${dt.getDate()}/${dt.getMonth() + 1}`,
    };
  });

  // Punto más cercano al mouse
  const getHoverPoints = useCallback((mouseXsvg) => {
    return series.map(s => {
      if (!s.data?.length) return null;
      const tx  = (ts) => padL + ((new Date(ts).getTime() - visMin) / visRange) * innerW;
      const vis = s.data.filter(d => { const t = new Date(d.ts).getTime(); return t >= visMin && t <= visMax; });
      if (!vis.length) return null;
      const pt  = vis.reduce((b, d) => Math.abs(tx(d.ts) - mouseXsvg) < Math.abs(tx(b.ts) - mouseXsvg) ? d : b);
      return { ...s, point: pt, svgX: tx(pt.ts), svgY: padT + innerH - (parseFloat(pt.amount) / maxAmt) * innerH };
    }).filter(Boolean);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, visMin, visRange, maxAmt]);

  const onMouseDown = useCallback((e) => { dragging.current = true; lastX.current = e.clientX; }, []);
  const onMouseUp   = useCallback(() => { dragging.current = false; }, []);

  const onMouseMove = useCallback((e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;

    if (dragging.current) {
      const dx = (e.clientX - lastX.current) / rect.width;
      lastX.current = e.clientX;
      setView(v => {
        const span = v.xMax - v.xMin;
        const n = Math.max(0, Math.min(1 - span, v.xMin - dx));
        return { xMin: n, xMax: n + span };
      });
      return;
    }

    // xPct: fracción dentro del área interna (0..1)
    const rawX    = e.clientX - rect.left;
    const xPct    = (rawX - rect.width * padL / W) / (rect.width * innerW / W);
    if (xPct < 0 || xPct > 1) { setHover(null); return; }

    const mouseXsvg = padL + xPct * innerW;
    const pts = getHoverPoints(mouseXsvg);
    if (pts.length) {
      setHover({
        pts,
        pixelX:   rawX,                  // px desde izquierda del SVG/contenedor
        pixelY:   e.clientY - rect.top,  // px desde arriba del SVG
        svgWidth: rect.width,
      });
    } else {
      setHover(null);
    }
  }, [getHoverPoints]);

  const onWheel = useCallback((e) => {
    e.preventDefault();
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx     = (e.clientX - rect.left - rect.width * padL / W) / (rect.width * innerW / W);
    const factor = e.deltaY > 0 ? 1.25 : 0.8;
    setView(v => {
      const span   = Math.min(1, Math.max(0.04, (v.xMax - v.xMin) * factor));
      const center = v.xMin + cx * (v.xMax - v.xMin);
      let lo = center - span * Math.max(0, Math.min(1, cx));
      let hi = lo + span;
      if (lo < 0) { hi -= lo; lo = 0; }
      if (hi > 1) { lo -= hi - 1; hi = 1; }
      return { xMin: Math.max(0, lo), xMax: Math.min(1, hi) };
    });
  }, []);

  const isZoomed  = view.xMin > 0.005 || view.xMax < 0.995;
  const flipLeft  = hover ? hover.pixelX / (hover.svgWidth || 600) > 0.58 : false;
  const tipLeft   = hover ? hover.pixelX + (flipLeft ? -164 : 12) : 0;
  const tipTop    = hover ? Math.max(4, Math.min(hover.pixelY - 20, innerH - 60)) : 0;

  return (
    <div className="relative select-none" onMouseLeave={() => setHover(null)}>
      {isZoomed && (
        <button onClick={() => setView({ xMin: 0, xMax: 1 })}
          className="absolute top-0 right-0 text-xs hover:underline z-10 pr-1"
          style={{ color: 'var(--accent)' }}>
          Reset zoom
        </button>
      )}

      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`}
        className="w-full cursor-grab active:cursor-grabbing"
        style={{ display: 'block', touchAction: 'none' }}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove}
        onMouseUp={onMouseUp} onMouseLeave={() => setHover(null)}
        onWheel={onWheel}
      >
        <defs>
          <clipPath id="sc-clip">
            <rect x={padL} y={padT} width={innerW} height={innerH} />
          </clipPath>
        </defs>

        {/* Grid horizontal + labels superpuestos al borde izquierdo */}
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={t.y} x2={W - padR} y2={t.y} stroke="var(--border)" strokeWidth={0.7} />
            <text x={padL + 3} y={t.y - 2} textAnchor="start" fontSize={7} fill="var(--text-muted)" fillOpacity={0.85}>
              {t.label}
            </text>
          </g>
        ))}

        {/* Series recortadas al área de datos */}
        <g clipPath="url(#sc-clip)">
          {series.map(s => {
            if (!s.data?.length) return null;
            return (
              <g key={s.status}>
                <path d={buildPath(s.data, visMin, visRange, maxAmt, true)}
                  fill={s.color} fillOpacity={0.12} />
                <path d={buildPath(s.data, visMin, visRange, maxAmt, false)}
                  fill="none" stroke={s.color} strokeWidth={1.8} strokeLinejoin="round" />
              </g>
            );
          })}

          {/* Crosshair + puntos hover (también recortados) */}
          {hover?.pts?.map(p => (
            <g key={p.status}>
              <line x1={p.svgX} y1={padT} x2={p.svgX} y2={padT + innerH}
                stroke={p.color} strokeWidth={0.8} strokeDasharray="4,3" opacity={0.45} />
              <circle cx={p.svgX} cy={p.svgY} r={4} fill={p.color} />
            </g>
          ))}
        </g>

        {/* X labels */}
        {xLabels.map((l, i) => (
          <text key={i} x={l.x} y={H - padB + 14}
            textAnchor="middle" fontSize={8} fill="var(--text-muted)">
            {l.label}
          </text>
        ))}
      </svg>

      {/* Tooltip — posición en píxeles exactos del mouse sobre el SVG */}
      {hover?.pts?.length > 0 && (
        <div className="absolute pointer-events-none z-20 border rounded-lg px-3 py-2 text-xs shadow-xl"
          style={{
            background:   'var(--bg-surface)',
            borderColor:  'var(--border)',
            left:         tipLeft,
            top:          tipTop,
            minWidth:     152,
          }}>
          <p className="font-mono mb-1.5" style={{ color: 'var(--text-muted)' }}>
            {granularity === 'hour'
              ? new Date(hover.pts[0].point.ts).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
              : new Date(hover.pts[0].point.ts).toLocaleDateString('es-AR')}
          </p>
          {hover.pts.map(p => (
            <div key={p.status} className="flex items-center gap-2 leading-5">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color }} />
              <span style={{ color: 'var(--text-secondary)' }}>{p.label}:</span>
              <span className="font-semibold" style={{ color: p.color }}>{fmt(p.point.amount)}</span>
              <span style={{ color: 'var(--text-muted)' }}>({fmtNum(p.point.count)})</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>Scroll = zoom · Drag = mover</p>
    </div>
  );
}

/* ── Componente principal ──────────────────────────────────────────── */
export default function SalesLineChart({ clientId, isSuperAdmin }) {
  const [storeId,        setStoreId]        = useState('');
  const [status,         setStatus]         = useState('processed');
  const [paymentMethod,  setPaymentMethod]  = useState('');
  const [granularity,    setGranularity]    = useState('day');
  const [days,           setDays]           = useState(30);

  const { data: stores } = useStores();
  const resolvedClientId = isSuperAdmin ? clientId : null;

  const isMulti = status === '';
  const base = { clientId: resolvedClientId, storeId, paymentMethod, granularity, days };

  const q0 = useSeries({ ...base, status: 'processed', enabled: isMulti });
  const q1 = useSeries({ ...base, status: 'canceled',  enabled: isMulti });
  const q2 = useSeries({ ...base, status: 'expired',   enabled: isMulti });
  const qS = useSeries({ ...base, status,              enabled: !isMulti });

  const isLoading = isMulti ? (q0.isLoading || q1.isLoading || q2.isLoading) : qS.isLoading;

  const series = isMulti
    ? STATUS_SERIES.map((s, i) => ({ ...s, data: [q0, q1, q2][i].data || [] }))
    : [{ status, color: STATUS_COLOR[status] || '#10b981', label: STATUS_OPTIONS.find(o => o.value === status)?.label || status, data: qS.data || [] }];

  return (
    <div className="card space-y-3">
      {/* Encabezado + filtros en una sola fila */}
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs font-semibold shrink-0 mr-1" style={{ color: 'var(--text-muted)' }}>
          VENTAS
        </p>

        <select className="input-base text-xs py-1 px-2 h-7 min-w-0 flex-1 max-w-[180px]"
          value={storeId} onChange={e => setStoreId(e.target.value)}>
          <option value="">Todas las sucursales</option>
          {(stores || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <select className="input-base text-xs py-1 px-2 h-7 min-w-0"
          value={status} onChange={e => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <select className="input-base text-xs py-1 px-2 h-7 min-w-0"
          value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
          {PM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <select className="input-base text-xs py-1 px-2 h-7 min-w-0"
          value={days} onChange={e => setDays(Number(e.target.value))}>
          {DAYS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <div className="flex rounded-lg border overflow-hidden shrink-0" style={{ borderColor: 'var(--border)' }}>
          {['day', 'hour'].map(g => (
            <button key={g} onClick={() => setGranularity(g)}
              className={`text-xs px-2.5 py-1 transition-colors ${granularity === g ? 'bg-accent text-white' : ''}`}
              style={granularity !== g ? { color: 'var(--text-muted)' } : {}}>
              {g === 'day' ? 'Día' : 'Hora'}
            </button>
          ))}
        </div>
      </div>

      {/* Leyenda multi-serie */}
      {isMulti && (
        <div className="flex gap-4">
          {STATUS_SERIES.map(s => (
            <div key={s.status} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
              <span className="inline-block w-3 h-0.5 rounded" style={{ background: s.color }} />
              {s.label}
            </div>
          ))}
        </div>
      )}

      {isLoading
        ? <div className="h-40 flex items-center justify-center"><Spinner /></div>
        : <LineChart series={series} granularity={granularity} />
      }
    </div>
  );
}

/* ── Gráfico de barras: total diario por medio de pago ─────────────── */
const BW = 560, BH = 155, bPadL = 2, bPadR = 2, bPadT = 14, bPadB = 28;
const bInnerW = BW - bPadL - bPadR;
const bInnerH = BH - bPadT - bPadB;

export function PaymentMethodChart({ clientId, isSuperAdmin }) {
  const [storeId, setStoreId] = useState('');
  const [days,    setDays]    = useState(30);
  const [hover,   setHover]   = useState(null);
  const svgRef = useRef(null);

  const { data: stores } = useStores();
  const resolvedClientId = isSuperAdmin ? clientId : null;

  const base = { clientId: resolvedClientId, storeId, granularity: 'day', days, status: 'processed' };

  const qCr = useSeries({ ...base, paymentMethod: 'credit_card' });
  const qDb = useSeries({ ...base, paymentMethod: 'debit_card'  });
  const qQr = useSeries({ ...base, paymentMethod: 'qr'          });

  const isLoading = qCr.isLoading || qDb.isLoading || qQr.isLoading;

  // Unir fechas de las tres series
  const dateMap = {};
  [[qCr.data, 'credit_card'], [qDb.data, 'debit_card'], [qQr.data, 'qr']].forEach(([rows, pm]) => {
    (rows || []).forEach(r => {
      const d = String(r.ts).slice(0, 10);
      if (!dateMap[d]) dateMap[d] = { credit_card: 0, debit_card: 0, qr: 0, count: 0 };
      dateMap[d][pm] += parseFloat(r.amount);
      dateMap[d].count += r.count;
    });
  });
  const dates = Object.keys(dateMap).sort();

  // Máximo de la suma de las tres barras en un día
  const maxTotal = Math.max(...dates.map(d => dateMap[d].credit_card + dateMap[d].debit_card + dateMap[d].qr), 1);

  const barW    = dates.length > 0 ? Math.max(2, (bInnerW / dates.length) * 0.7) : 8;
  const barGap  = dates.length > 0 ? bInnerW / dates.length : bInnerW;
  const yTicks  = [0, 0.5, 1].map(f => ({ y: bPadT + bInnerH - f * bInnerH, label: fmt(f * maxTotal) }));

  const onMouseMove = useCallback((e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || !dates.length) return;
    const xPct = (e.clientX - rect.left) / rect.width;
    const idx  = Math.round(xPct * (dates.length - 1));
    if (idx < 0 || idx >= dates.length) { setHover(null); return; }
    setHover({ idx, pixelX: e.clientX - rect.left, pixelY: e.clientY - rect.top, svgWidth: rect.width });
  }, [dates.length]);

  return (
    <div className="card space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs font-semibold shrink-0 mr-1" style={{ color: 'var(--text-muted)' }}>
          MEDIOS DE PAGO / DÍA
        </p>

        <select className="input-base text-xs py-1 px-2 h-7 min-w-0 flex-1 max-w-[180px]"
          value={storeId} onChange={e => setStoreId(e.target.value)}>
          <option value="">Todas las sucursales</option>
          {(stores || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <select className="input-base text-xs py-1 px-2 h-7 min-w-0"
          value={days} onChange={e => setDays(Number(e.target.value))}>
          {DAYS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        {/* Leyenda */}
        <div className="flex gap-3 ml-auto">
          {PM_SERIES.map(s => (
            <div key={s.pm} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
              {s.label}
            </div>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="h-36 flex items-center justify-center"><Spinner /></div>
      ) : !dates.length ? (
        <p className="text-sm py-8 text-center" style={{ color: 'var(--text-muted)' }}>Sin datos para el período</p>
      ) : (
        <div className="relative select-none" onMouseLeave={() => setHover(null)}>
          <svg ref={svgRef} viewBox={`0 0 ${BW} ${BH}`}
            className="w-full" style={{ display: 'block' }}
            onMouseMove={onMouseMove} onMouseLeave={() => setHover(null)}>
            <defs>
              <clipPath id="pm-clip">
                <rect x={bPadL} y={bPadT} width={bInnerW} height={bInnerH} />
              </clipPath>
            </defs>

            {/* Y grid */}
            {yTicks.map((t, i) => (
              <g key={i}>
                <line x1={bPadL} y1={t.y} x2={BW - bPadR} y2={t.y} stroke="var(--border)" strokeWidth={0.7} />
                <text x={bPadL + 3} y={t.y - 2} textAnchor="start" fontSize={7} fill="var(--text-muted)" fillOpacity={0.85}>
                  {t.label}
                </text>
              </g>
            ))}

            {/* Barras apiladas */}
            <g clipPath="url(#pm-clip)">
              {dates.map((d, i) => {
                const cx   = bPadL + (i + 0.5) * barGap;
                const x    = cx - barW / 2;
                const vals = [
                  { pm: 'credit_card', color: PM_SERIES[0].color, v: dateMap[d].credit_card },
                  { pm: 'debit_card',  color: PM_SERIES[1].color, v: dateMap[d].debit_card  },
                  { pm: 'qr',          color: PM_SERIES[2].color, v: dateMap[d].qr           },
                ];
                const isHovered = hover?.idx === i;
                let yBase = bPadT + bInnerH;
                return (
                  <g key={d} opacity={hover && !isHovered ? 0.4 : 1}>
                    {vals.map(({ pm, color, v }) => {
                      const h = (v / maxTotal) * bInnerH;
                      if (h < 0.5) return null;
                      yBase -= h;
                      return <rect key={pm} x={x} y={yBase} width={barW} height={h} fill={color} rx={1} />;
                    })}
                  </g>
                );
              })}
            </g>

            {/* X labels — cada N días */}
            {dates.filter((_, i) => i % Math.max(1, Math.floor(dates.length / 6)) === 0 || i === dates.length - 1).map((d, i) => {
              const [, m, day] = d.split('-');
              const cx = bPadL + (dates.indexOf(d) + 0.5) * barGap;
              return (
                <text key={i} x={Math.min(Math.max(cx, bPadL + 4), BW - bPadR - 4)}
                  y={BH - bPadB + 14} textAnchor="middle" fontSize={8} fill="var(--text-muted)">
                  {`${parseInt(day)}/${parseInt(m)}`}
                </text>
              );
            })}
          </svg>

          {/* Tooltip */}
          {hover && (() => {
            const d   = dates[hover.idx];
            const row = dateMap[d];
            if (!row) return null;
            const flip = hover.pixelX / (hover.svgWidth || 600) > 0.58;
            const dt   = new Date(d + 'T12:00:00');
            return (
              <div className="absolute pointer-events-none z-20 border rounded-lg px-3 py-2 text-xs shadow-xl"
                style={{
                  background: 'var(--bg-surface)',
                  borderColor: 'var(--border)',
                  left: hover.pixelX + (flip ? -164 : 12),
                  top:  Math.max(4, hover.pixelY - 20),
                  minWidth: 152,
                }}>
                <p className="font-mono mb-1.5" style={{ color: 'var(--text-muted)' }}>
                  {dt.toLocaleDateString('es-AR')}
                </p>
                {PM_SERIES.map(s => (
                  <div key={s.pm} className="flex items-center gap-2 leading-5">
                    <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: s.color }} />
                    <span style={{ color: 'var(--text-secondary)' }}>{s.label}:</span>
                    <span className="font-semibold" style={{ color: s.color }}>{fmt(row[s.pm])}</span>
                  </div>
                ))}
                <div className="mt-1 pt-1 flex justify-between" style={{ borderTop: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                  <span>Total</span>
                  <span className="font-semibold" style={{ color: 'var(--text)' }}>
                    {fmt(row.credit_card + row.debit_card + row.qr)}
                  </span>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
